import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

function Shell({ title, children }) {
  return (
    <div className="min-h-screen bg-paper py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-steel hover:text-ink mb-4">
          <ArrowLeft size={14} weight="bold" /> Volver
        </Link>
        <div className="brutal-card bg-white p-8 lg:p-12">
          <div className="overline">LEGAL</div>
          <h1 className="font-display font-black text-4xl tracking-tight mb-6">{title}</h1>
          <div className="prose prose-slate max-w-none
            prose-headings:font-display prose-headings:font-bold prose-headings:tracking-tight
            prose-h2:text-xl prose-h2:mt-8 prose-h2:border-b-2 prose-h2:border-ink prose-h2:pb-2
            prose-p:text-sm prose-li:text-sm prose-strong:text-ink">
            {children}
          </div>
          <div className="text-xs text-steel font-mono mt-10 pt-4 border-t-2 border-slate-200">
            Última actualización: mayo 2026
          </div>
        </div>
      </div>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <Shell title="Política de privacidad">
      <h2>1. Responsable del tratamiento</h2>
      <p>
        El responsable del tratamiento de tus datos personales es <b>Factlify</b>
        (en adelante, "Factlify" o "nosotros"), con dominio <b>factlify.es</b>.
        Para cualquier consulta sobre privacidad, escríbenos a{" "}
        <a href="mailto:soporte@factlify.es">soporte@factlify.es</a>.
      </p>

      <h2>2. Datos que recogemos</h2>
      <ul>
        <li><b>Datos de cuenta</b>: email y nombre, facilitados por ti al registrarte mediante enlace mágico (magic link).</li>
        <li><b>Datos fiscales y de negocio</b>: nombre de empresa, NIF, dirección, teléfono, logo, numeración.</li>
        <li><b>Datos de tus clientes y facturas</b>: introducidos por ti para gestionar tu actividad.</li>
        <li><b>Datos de pago</b>: procesados directamente por <b>Stripe</b> (no almacenamos tarjetas).</li>
        <li><b>Datos técnicos</b>: IP, navegador, idioma, logs de error (con consentimiento).</li>
        <li><b>Imágenes de tickets/gastos</b>: enviadas a la API de <b>Anthropic (Claude)</b> para OCR solo si usas la función de escaneado automático. No se almacenan en sus servidores.</li>
      </ul>

      <h2>3. Base legal y finalidad</h2>
      <ul>
        <li><b>Ejecución del servicio</b> (Art. 6.1.b RGPD) — prestación del ERP/facturación.</li>
        <li><b>Obligación legal</b> (Art. 6.1.c) — conservación de facturas 4 años (LGT), Verifactu.</li>
        <li><b>Interés legítimo</b> (Art. 6.1.f) — seguridad, prevención de fraude, analítica agregada.</li>
        <li><b>Consentimiento</b> (Art. 6.1.a) — cookies analíticas, envíos promocionales.</li>
      </ul>

      <h2>4. Cesiones y encargados</h2>
      <p>Compartimos datos únicamente con los siguientes encargados del tratamiento, sujetos a DPA:</p>
      <ul>
        <li><b>Stripe Inc.</b> — pagos y suscripciones.</li>
        <li><b>Resend Inc.</b> — envío de emails transaccionales (magic link, presupuestos, facturas).</li>
        <li><b>MongoDB Atlas</b> — almacenamiento cifrado (AWS eu-west-1).</li>
        <li><b>Railway</b> — infraestructura de backend (servidores UE).</li>
        <li><b>Vercel</b> — infraestructura de frontend (CDN).</li>
        <li><b>Anthropic (Claude API)</b> — OCR de tickets de gasto, solo si el usuario activa esta función.</li>
        <li><b>AEAT</b> — remisión de registros de facturación en modalidad Veri*Factu (cuando aplique).</li>
        <li><b>PostHog</b> — análisis anónimo de uso (solo si el usuario acepta cookies analíticas).</li>
      </ul>
      <p>No vendemos ni compartimos datos con terceros con fines comerciales.</p>

      <h2>5. Conservación</h2>
      <ul>
        <li>Facturas: <b>4 años</b> (obligación fiscal española).</li>
        <li>Datos de cuenta: mientras dure la relación + 90 días tras cancelación.</li>
        <li>Logs técnicos: 30 días.</li>
      </ul>

      <h2>6. Tus derechos (RGPD)</h2>
      <p>Tienes derecho de acceso, rectificación, supresión, oposición, portabilidad y limitación.
        Ejerce tus derechos escribiendo a <a href="mailto:soporte@factlify.es">soporte@factlify.es</a>.
        También puedes reclamar ante la <a href="https://www.aepd.es" target="_blank" rel="noreferrer">
        Agencia Española de Protección de Datos (AEPD)</a>.</p>

      <h2>7. Seguridad</h2>
      <p>Aplicamos medidas técnicas y organizativas: cifrado TLS 1.3 en tránsito, cifrado AES-256
        en reposo, backups diarios, acceso por roles, registros de actividad.</p>

      <h2>8. Menores</h2>
      <p>Factlify no está dirigido a menores de 16 años.</p>

      <h2>9. Cambios</h2>
      <p>Notificaremos por email cualquier cambio material en esta política con al menos 30 días de antelación.</p>
    </Shell>
  );
}

export function TermsPage() {
  return (
    <Shell title="Términos y condiciones">
      <h2>1. Objeto</h2>
      <p>Factlify ofrece una plataforma SaaS de gestión para autónomos y pymes (reformas, fontanería,
        electricidad, pintura, carpintería y otros oficios y servicios): clientes, presupuestos,
        facturación electrónica Veri*Factu, cobros y gastos. Incluye un modo avanzado opcional para
        empresas de cerramientos. Al crear una cuenta aceptas estos términos.</p>

      <h2>2. Planes y precios</h2>
      <p>Los planes y precios vigentes se muestran en la página de <a href="/billing">Suscripción</a> y en{" "}
        <a href="/">factlify.es</a>. Los precios están expresados en EUR e incluyen el IVA aplicable.
        La facturación es mensual por adelantado. El periodo de prueba gratis es de 14 días sin necesidad
        de tarjeta de crédito. Factlify se reserva el derecho de modificar precios con un preaviso mínimo
        de 30 días por email.</p>

      <h2>3. Uso aceptable</h2>
      <ul>
        <li>No usar la plataforma para actividades ilegales o fraudulentas.</li>
        <li>No intentar vulnerar la seguridad, hacer scraping masivo o sobrecargar la API.</li>
        <li>No emitir facturas falsas ni evadir obligaciones fiscales.</li>
      </ul>

      <h2>4. Propiedad intelectual</h2>
      <p>Factlify mantiene la propiedad intelectual del software y la marca.
        Los datos que introduces (clientes, facturas, etc.) son de tu propiedad.
        Podrás exportarlos en cualquier momento.</p>

      <h2>5. Limitación de responsabilidad</h2>
      <p>Factlify se ofrece "tal cual" durante la fase beta. No nos responsabilizamos de daños indirectos
        derivados del uso del servicio. Nuestra responsabilidad máxima se limita al importe pagado en los 12 meses anteriores.</p>

      <h2>6. Garantías fiscales</h2>
      <p>Factlify es una herramienta de apoyo. La responsabilidad última de la correcta
        presentación de impuestos y cumplimiento normativo (Modelo 303, Verifactu, etc.) recae en el usuario.
        Recomendamos revisar con tu gestor/asesor fiscal.</p>

      <h2>7. Cancelación</h2>
      <p>Puedes cancelar en cualquier momento desde <b>Suscripción → Gestionar</b>. No hay permanencia.
        Tras cancelar mantienes acceso hasta fin del periodo pagado.</p>

      <h2>8. Ley aplicable</h2>
      <p>Estos términos se rigen por la legislación española. Jurisdicción: tribunales de España.</p>
    </Shell>
  );
}

export function LegalPage() {
  return (
    <Shell title="Aviso legal">
      <h2>Datos identificativos del titular (Art. 10 LSSI)</h2>
      <p>
        En cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información
        y del Comercio Electrónico (LSSI-CE), se facilitan los siguientes datos identificativos:
      </p>
      <ul>
        <li><b>Titular del proyecto</b>: Factlify (proyecto en fase beta).</li>
        <li><b>Identificación fiscal</b>: Factlify se encuentra en fase de <b>beta cerrada</b> y todavía
          no opera como actividad económica registrada. Los datos fiscales completos del titular
          (nombre o razón social, NIF/CIF y domicilio fiscal) se publicarán en este aviso antes del
          inicio de la actividad comercial y están disponibles, mientras tanto, bajo solicitud en{" "}
          <a href="mailto:soporte@factlify.es">soporte@factlify.es</a>.</li>
        <li><b>Email de contacto</b>: <a href="mailto:soporte@factlify.es">soporte@factlify.es</a></li>
        <li><b>Dominio</b>: factlify.es</li>
      </ul>
      <p className="text-xs text-steel">
        Durante la fase beta el acceso se ofrece con fines de prueba y mejora del producto. Antes de
        iniciar el cobro de suscripciones se completará esta identificación fiscal y se revisará el
        marco legal con asesoría profesional.
      </p>
      <p>La información ofrecida en factlify.es tiene carácter general. El acceso y uso del sitio
        implican la aceptación de este aviso legal y de las políticas vinculadas
        (<a href="/legal/privacidad">Privacidad</a>,{" "}
        <a href="/legal/terminos">Términos</a>,{" "}
        <a href="/legal/cookies">Cookies</a>,{" "}
        <a href="/legal/licencias">Licencias de terceros</a>).</p>

      <h2>Objeto del servicio</h2>
      <p>Factlify es una plataforma SaaS de gestión para autónomos y pymes (reformas, fontanería,
        electricidad, pintura, carpintería y otros oficios y servicios): clientes, presupuestos,
        facturación electrónica Veri*Factu, cobros y gastos. Incluye un modo avanzado opcional para
        empresas de cerramientos.</p>

      <h2>Propiedad intelectual</h2>
      <p>Todos los contenidos de este sitio (textos, código, diseño, marca) son propiedad de su titular
        salvo mención expresa. Queda prohibida la reproducción total o parcial sin autorización escrita.</p>
      <p>Las marcas, referencias y catálogos de terceros que puedan aparecer dentro de la aplicación se usan
        únicamente para identificación técnica o compatibilidad operativa. Factlify no está afiliada,
        patrocinada ni autorizada por dichos titulares salvo acuerdo expreso.</p>

      <h2>Exención de responsabilidad</h2>
      <p>Factlify no se responsabiliza del contenido de enlaces externos. El servicio puede sufrir
        interrupciones por mantenimiento que se anunciarán con antelación cuando sea posible.</p>

      <h2>Resolución de litigios en línea (UE)</h2>
      <p>Conforme al Reglamento (UE) 524/2013, los consumidores de la UE pueden acceder a la plataforma
        de resolución de litigios en línea:{" "}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">
          ec.europa.eu/consumers/odr
        </a>.
      </p>
    </Shell>
  );
}

export function DPAPage() {
  return (
    <Shell title="Acuerdo de Tratamiento de Datos (DPA)">
      <p className="text-sm text-steel">
        Plantilla del DPA que firmamos con clientes empresa/autónomos a petición.
        Para recibirlo formalizado, escribe a <a href="mailto:soporte@factlify.es">soporte@factlify.es</a>{" "}
        indicando los datos fiscales del responsable y te lo enviamos en 24 h.
      </p>

      <h2>1. Partes</h2>
      <p><b>Responsable del tratamiento</b>: el cliente / usuario de Factlify.<br/>
         <b>Encargado del tratamiento</b>: Factlify (titular del dominio factlify.es).</p>

      <h2>2. Objeto</h2>
      <p>Este acuerdo regula el tratamiento de datos personales que Factlify realiza
        por cuenta del Responsable en el marco de la prestación del SaaS de gestión
        de presupuestos, facturas, gastos y citas.</p>

      <h2>3. Categorías de datos y de afectados</h2>
      <ul>
        <li><b>Tipos de datos</b>: identificativos, fiscales, contacto, datos de actividad económica.</li>
        <li><b>Categorías de afectados</b>: clientes finales del Responsable, sus colaboradores y proveedores.</li>
      </ul>

      <h2>4. Duración</h2>
      <p>Mientras dure la relación contractual + 4 años de conservación legal exigidos
        por la normativa fiscal española y la Ley Crea y Crece.</p>

      <h2>5. Subencargados autorizados</h2>
      <ul>
        <li><b>Stripe Inc.</b> — pagos y suscripciones.</li>
        <li><b>Resend Inc.</b> — emails transaccionales (magic link, presupuestos, facturas).</li>
        <li><b>MongoDB Atlas (AWS eu-west-1)</b> — base de datos cifrada.</li>
        <li><b>Railway</b> — infraestructura de backend.</li>
        <li><b>Vercel</b> — CDN / frontend.</li>
        <li><b>Anthropic (Claude API)</b> — OCR de tickets de gasto (solo si lo activa el Responsable).</li>
        <li><b>PostHog</b> — analítica de uso anónima (solo si el Responsable acepta cookies analíticas).</li>
      </ul>
      <p>Cualquier nuevo subencargado se notificará con 30 días de antelación.</p>

      <h2>6. Medidas de seguridad (Art. 32 RGPD)</h2>
      <ul>
        <li>Cifrado en tránsito (TLS 1.3, Let's Encrypt).</li>
        <li>Aislamiento estricto multitenant (workspace_id en TODAS las consultas, auditado).</li>
        <li>Sesiones HTTP-only (secure en preview/producción; configuración local adaptada para desarrollo).</li>
        <li>Auditoría inmutable de cambios de estado de factura (Ley Crea y Crece).</li>
        <li>Backups diarios cifrados con retención 30 días.</li>
        <li>Acceso al panel de soporte con MFA y registro de logs.</li>
      </ul>

      <h2>7. Notificación de brechas</h2>
      <p>Factlify se compromete a notificar al Responsable cualquier brecha de seguridad
        que afecte a sus datos en un plazo inferior a <b>72 horas</b> desde su detección,
        con la información necesaria para que el Responsable pueda cumplir el Art. 33 RGPD.</p>

      <h2>8. Auditoría</h2>
      <p>El Responsable puede solicitar una auditoría documental anual escribiendo a soporte@factlify.es.
        Para auditorías presenciales se requerirá acuerdo previo de fechas y NDA.</p>

      <h2>9. Devolución/destrucción</h2>
      <p>Al terminar el contrato, el Responsable puede solicitar (a) exportar todos los datos
        en CSV/JSON o (b) la destrucción definitiva. Por defecto se mantienen 90 días en cuarentena
        antes del borrado salvo solicitud explícita anterior.</p>

      <h2>10. Ley aplicable</h2>
      <p>Legislación española. Jurisdicción: tribunales de Barcelona.</p>
    </Shell>
  );
}

export function CookiesPage() {
  return (
    <Shell title="Política de cookies">
      <h2>¿Qué son?</h2>
      <p>Las cookies son pequeños archivos que se almacenan en tu navegador cuando visitas un sitio web.</p>

      <h2>Tipos de cookies que usamos</h2>
      <table>
        <thead>
          <tr><th>Cookie</th><th>Tipo</th><th>Finalidad</th><th>Duración</th></tr>
        </thead>
        <tbody>
          <tr><td>session_token</td><td>Técnica</td><td>Autenticación</td><td>7 días</td></tr>
          <tr><td>active_workspace_id</td><td>Técnica</td><td>Workspace activo</td><td>Persistente</td></tr>
          <tr><td>factlify_cookies_v1</td><td>Técnica</td><td>Guarda tu decisión sobre cookies</td><td>Persistente</td></tr>
          <tr><td>ph_*</td><td>Analítica</td><td>PostHog — análisis anónimo de uso (solo si aceptas)</td><td>1 año</td></tr>
        </tbody>
      </table>

      <h2>Cómo gestionar cookies</h2>
      <p>Puedes aceptar o rechazar las cookies analíticas en el banner que aparece al entrar.
        Las cookies técnicas son necesarias y no requieren consentimiento (Art. 22 LSSI).
        Puedes borrar todas las cookies desde la configuración de tu navegador.</p>
    </Shell>
  );
}

export function LicensesPage() {
  return (
    <Shell title="Licencias de software de terceros">
      <p>Factlify se construye sobre software de código abierto. Agradecemos y reconocemos
        el trabajo de sus autores. Todo el software de terceros que incorporamos se usa bajo
        licencias permisivas que permiten su uso comercial.</p>

      <h2>Composición de las dependencias</h2>
      <p>La inmensa mayoría del software de terceros está bajo licencia <b>MIT</b>, junto con
        <b> BSD</b>, <b>ISC</b>, <b>Apache-2.0</b> y otras licencias permisivas equivalentes.</p>
      <ul>
        <li><b>Frontend</b>: 1.373 paquetes (mayoritariamente MIT).</li>
        <li><b>Backend</b>: 142 paquetes (Apache-2.0, MIT, BSD).</li>
      </ul>

      <h2>Componentes con licencia copyleft débil</h2>
      <p>Algunos componentes auxiliares (<code>certifi</code>, <code>pathspec</code>, <code>tqdm</code>)
        incluyen ficheros bajo <b>MPL-2.0</b>, una licencia copyleft a nivel de fichero. Factlify
        los utiliza sin modificar, por lo que no genera obligaciones adicionales. La biblioteca
        <code> node-forge</code> es de doble licencia (BSD-3-Clause o GPL-2.0) y se usa bajo la
        rama <b>BSD-3-Clause</b>.</p>
      <p><b>Factlify no incorpora ningún componente bajo licencia GPL, AGPL o SSPL.</b> Los
        controladores de base de datos <code>pymongo</code>/<code>motor</code> son Apache-2.0;
        el servidor MongoDB (SSPL) se consume como servicio gestionado (Atlas) y no se redistribuye.</p>

      <h2>Avisos completos</h2>
      <p>El listado completo de paquetes con sus avisos de licencia y textos de copyright está
        disponible aquí:{" "}
        <a href="/licenses.txt" target="_blank" rel="noreferrer">/licenses.txt</a>.</p>
      <p className="text-xs text-steel">Para cualquier consulta sobre licencias escríbenos a{" "}
        <a href="mailto:soporte@factlify.es">soporte@factlify.es</a>.</p>
    </Shell>
  );
}
